import type { ExportFormat, ExportScope } from './download'
import type { PartKind } from './grid'
import { BID_EXPORT_FORMATS, allowedExportFormats, type ArtefactType } from './artefact-type'

/**
 * What the Export dialog offers at each scope (AB#212): the formats the backend ACCEPTS, nothing
 * curated on top. Mirrors backend/app/services.ts#exportDocument, which stays the authority (its
 * 422 `unsupported_for_kind` is the safety net); see docs/spec/20-workspace.md "PowerPoint export":
 *
 *   part         slides part (or slides artefact)   → PowerPoint only
 *                prose / grid part                  → Word, web page, Markdown, Excel (grid: Excel first)
 *   deliverable  every counted part a slide         → PowerPoint only (one deck)
 *                slides beside prose / grid parts   → refused ("mixes slides and document parts")
 *                no slides part                     → the type's formats (all grids: Excel first)
 *   bid          any deliverable mixed              → refused (the ZIP fails the same way)
 *                otherwise                          → the prose formats; decks come as .pptx in the ZIP
 *
 * Review parts never count toward a mix (backend/domain/export.ts#isReviewPart).
 */

/** The formats offered, and the one checked until the member picks another. */
export interface OfferedFormats {
  formats: readonly ExportFormat[]
  preferred: ExportFormat
}

export type ScopeAvailability = ({ ok: true } & OfferedFormats) | { ok: false; reason: 'mixed_deliverable' | 'mixed_bid' }

/** The kinds of a deliverable's parts that count toward an export — its review parts left out. */
export function exportedKinds(parts: readonly { partId: string; kind?: PartKind }[]): PartKind[] {
  return parts.filter((p) => p.partId !== 'review').map((p) => p.kind ?? 'markdown')
}

/** A deliverable holding slides beside document (prose / grid) parts: no single file holds both. */
export function mixesSlides(kinds: readonly PartKind[]): boolean {
  return kinds.includes('slides') && kinds.some((k) => k !== 'slides')
}

function ofType(type: ArtefactType, preferred: ExportFormat): { ok: true } & OfferedFormats {
  const formats = allowedExportFormats(type)
  return { ok: true, formats, preferred: formats.includes(preferred) ? preferred : formats[0]! }
}

const PPTX_ONLY: { ok: true } & OfferedFormats = { ok: true, formats: ['pptx'], preferred: 'pptx' }

/** Part scope — never refused: a part, even one of a mixed deliverable, exports on its own. */
export function partFormats(partKind: PartKind | undefined, artefactType: ArtefactType): { ok: true } & OfferedFormats {
  if (partKind === 'slides') return PPTX_ONLY
  return ofType(artefactType, partKind === 'grid' ? 'xlsx' : 'docx')
}

/**
 * @param partKind         the open part's kind
 * @param artefactType     its deliverable's type
 * @param deliverableKinds the deliverable's counted part kinds (`exportedKinds`), or null while the
 *                         artefacts query loads — the open part's kind then stands in for them
 * @param bidMixed         whether any deliverable of the bid mixes slides and document parts
 */
export function availableFormats(
  scope: ExportScope,
  partKind: PartKind | undefined,
  artefactType: ArtefactType,
  deliverableKinds: readonly PartKind[] | null,
  bidMixed = false,
): ScopeAvailability {
  if (scope === 'bid') {
    if (bidMixed) return { ok: false, reason: 'mixed_bid' }
    return { ok: true, formats: BID_EXPORT_FORMATS, preferred: 'docx' }
  }
  if (scope === 'part') return partFormats(partKind, artefactType)
  const kinds = deliverableKinds ?? (partKind ? [partKind] : [])
  if (mixesSlides(kinds)) return { ok: false, reason: 'mixed_deliverable' }
  if (kinds.length > 0 && kinds.every((k) => k === 'slides')) return PPTX_ONLY
  return ofType(artefactType, kinds.length > 0 && kinds.every((k) => k === 'grid') ? 'xlsx' : 'docx')
}
